<template>
  <div class="mapping-rules-actions-entry__caption">
    {{ title }}
  </div>
  <Block>
    <EntryField
      v-if="entryAccount?.default || entryAccount?.value"
      v-model="modelAccount as MappingRuleActionField"
      :settings="entryAccount"
      :label="TRANSACTION_ENTRY_FIELD_LABEL[TRANSACTION_ENTRY_FIELD.ACCOUNT_ID]"
      :json-data
      :disabled
    />
    <EntryAccount
      v-else-if="entryAccount"
      v-model="modelAccount as MappingRuleActionAccount"
      :settings="entryAccount"
      :label="TRANSACTION_ENTRY_FIELD_LABEL[TRANSACTION_ENTRY_FIELD.ACCOUNT_ID]"
      :disabled
    />
    <EntryField
      v-if="entryAmount"
      v-model="modelAmount"
      data-refid="mappingRuleEntryAmount"
      :settings="entryAmount"
      :label="TRANSACTION_ENTRY_FIELD_LABEL[TRANSACTION_ENTRY_FIELD.AMOUNT]"
      :json-data
      :disabled
    />
    <EntryField
      v-if="entryAsset"
      v-model="modelAsset"
      data-refid="mappingRuleEntryAsset"
      :settings="entryAsset"
      :label="TRANSACTION_ENTRY_FIELD_LABEL[TRANSACTION_ENTRY_FIELD.ASSET_ID]"
      :json-data
      :disabled
    />
  </Block>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import { TransactionSettings } from '@types'
import {
  MappingRuleAction,
  MappingRuleActionAccount,
  MappingRuleActionField,
} from './utils/types'

import { TRANSACTION_ENTRY_FIELD, TRANSACTION_ENTRY_FIELD_LABEL } from '@/const'

import EntryAccount from './components/EntryAccount.vue'
import EntryField from './components/EntryField.vue'
import Block from './components/Block.vue'

type Props = {
  settings: TransactionSettings['entries'][number]
  index: number
  jsonData?: Record<string, unknown>
  disabled?: boolean
}

const props = defineProps<Props>()

const modelValue = defineModel<MappingRuleAction>()

const title = computed(() => props.settings.title)

const entryAccount = computed(() => props.settings.account)
const entryAmount = computed(() => props.settings.amount)
const entryAsset = computed(() => props.settings.asset)

const modelAccount = computed({
  get() {
    return modelValue.value?.entries?.[props.index]?.account
  },
  set(account) {
    modelValue.value = {
      type: 'map to transaction',
      description: modelValue.value?.description ?? {},
      transaction_type: modelValue.value?.transaction_type,
      ...modelValue.value,
      entries: (modelValue.value?.entries ?? []).map((entry, index) =>
        index === props.index
          ? {
            ...entry,
            account,
          }
          : entry,
      ),
    }
  },
})

const modelAmount = computed({
  get() {
    return modelValue.value?.entries?.[props.index]?.amount
  },
  set(amount) {
    modelValue.value = {
      type: 'map to transaction',
      description: modelValue.value?.description ?? {},
      transaction_type: modelValue.value?.transaction_type,
      ...modelValue.value,
      entries: (modelValue.value?.entries ?? []).map((entry, index) =>
        index === props.index
          ? {
            ...entry,
            amount,
          }
          : entry,
      ),
    }
  },
})

const modelAsset = computed({
  get() {
    return modelValue.value?.entries?.[props.index]?.asset
  },
  set(asset) {
    modelValue.value = {
      type: 'map to transaction',
      description: modelValue.value?.description ?? {},
      transaction_type: modelValue.value?.transaction_type,
      ...modelValue.value,
      entries: (modelValue.value?.entries ?? []).map((entry, index) =>
        index === props.index
          ? {
            ...entry,
            asset,
          }
          : entry,
      ),
    }
  },
})
</script>

<style>
.mapping-rules-actions-entry {
  &__caption {
    @apply flex justify-center;
    @apply py-1;
    @apply rounded;
    @apply font-medium text-sm sm:text-xs;
    @apply bg-indigo-50 dark:bg-indigo-900;
    @apply text-indigo-600 dark:text-indigo-400;
  }
}
</style>
